/* Plantica — blog listing

   Every article is already in the page, rendered by Liquid. Filtering, sorting
   and „load more" only change what is visible, so none of them costs a request
   and none of them changes the URL — the SEO decision behind that is written up
   in plantica-blog-grid.liquid.

   Visibility is `hidden`, not a class, so a folded card is gone for assistive
   technology too and the counter cannot claim more than is on screen.

   The button is an anchor pointing at the blog's next page. While there are
   still folded cards it reveals them and the navigation is cancelled; once
   there are none and there is no next page it goes away. With JS off it is what
   it looks like: a link that works. */

(() => {
  if (customElements.get('plantica-blog')) return;

  /* „1 articol", „6 articole", „32 de articole" — Romanian picks the form from
     the last two digits. Same rule as the Liquid that renders the first state;
     if one changes, change both. */
  const noun = (n) => {
    if (n === 1) return 'articol';
    const rest = n % 100;
    return rest === 0 || rest >= 20 ? 'de articole' : 'articole';
  };

  class PlanticaBlog extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.plantica-blog-grid__list');
      if (!this.list) return;

      this.cells = Array.from(this.querySelectorAll('.plantica-blog-grid__cell'));
      this.filters = Array.from(this.querySelectorAll('.plantica-blog-grid__filter'));
      this.select = this.querySelector('.plantica-blog-grid__select');
      this.counter = this.querySelector('[data-count]');
      this.more = this.querySelector('[data-more]');

      this.step = parseInt(this.dataset.step, 10) || 6;
      this.initial = parseInt(this.dataset.initial, 10) || 6;
      this.total = parseInt(this.dataset.total, 10) || this.cells.length;
      this.next = this.dataset.next || '';

      this.filter = '';
      this.shown = this.initial;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.select) this.select.addEventListener('change', this.onSort.bind(this));

      this.render();
    }

    onClick(event) {
      const pill = event.target.closest('.plantica-blog-grid__filter');
      if (pill) {
        this.select_(pill);
        return;
      }

      const more = event.target.closest('[data-more]');
      if (!more) return;

      // Cards left to reveal: handle it here and stay on the page.
      if (this.matching().length > this.shown) {
        event.preventDefault();
        this.shown += this.step;
        this.render(true);
      }
      // Otherwise the anchor is left alone and the next page loads.
    }

    /* Left/right move between filters, as expected of a tablist. */
    onKeydown(event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

      const current = event.target.closest('.plantica-blog-grid__filter');
      if (!current) return;

      const index = this.filters.indexOf(current);
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const pill = this.filters[(index + step + this.filters.length) % this.filters.length];

      this.select_(pill);
      pill.focus();
    }

    select_(pill) {
      this.filters.forEach((item) => {
        const on = item === pill;
        item.classList.toggle('is-active', on);
        item.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      this.filter = pill.dataset.filter || '';
      // A new category starts from the top rather than keeping the depth
      // reached in the previous one.
      this.shown = this.initial;
      this.render();
    }

    onSort() {
      const newest = this.select.value !== 'oldest';
      const order = this.cells.slice().sort((a, b) => {
        const da = Number(a.dataset.date) || 0;
        const db = Number(b.dataset.date) || 0;
        return newest ? db - da : da - db;
      });

      const fragment = document.createDocumentFragment();
      order.forEach((cell) => fragment.appendChild(cell));
      this.list.appendChild(fragment);

      this.cells = order;
      this.shown = this.initial;
      this.render();
    }

    matching() {
      if (!this.filter) return this.cells;
      const key = '|' + this.filter + '|';
      return this.cells.filter((cell) => (cell.dataset.tags || '').indexOf(key) !== -1);
    }

    render(animate) {
      const matching = this.matching();
      const visible = matching.slice(0, this.shown);
      const before = new Set(this.cells.filter((cell) => !cell.hidden));

      this.cells.forEach((cell) => {
        cell.hidden = true;
      });

      visible.forEach((cell) => {
        cell.hidden = false;
      });

      if (animate) {
        const fresh = visible.filter((cell) => !before.has(cell));
        fresh.forEach((cell, i) => {
          cell.classList.add('is-revealed');
          cell.style.animationDelay = `${i * 60}ms`;
          cell.addEventListener(
            'animationend',
            () => {
              cell.classList.remove('is-revealed');
              cell.style.animationDelay = '';
            },
            { once: true }
          );
        });

        // Follow the growth instead of letting the button jump away under the
        // cursor. `nearest` scrolls only as far as it has to.
        if (fresh[0]) fresh[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      this.count(visible.length, this.filter ? matching.length : this.total);
      this.button(matching.length > this.shown);
    }

    count(shown, total) {
      if (!this.counter) return;
      const template = this.counter.dataset.template || '';
      this.counter.textContent = template
        .replace('{n}', shown)
        .replace('{total}', total)
        .replace('{articole}', noun(total));
    }

    button(hasLocal) {
      if (!this.more) return;
      // Nothing folded here and no page after this one: the button would be a
      // dead end, so the whole row goes.
      this.more.hidden = !hasLocal && !this.next;
    }
  }

  customElements.define('plantica-blog', PlanticaBlog);
})();
